import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Ruler, BarChart2 } from 'lucide-react';
import Header from '../../../components/layout/Header';
import Footer from '../../../components/layout/Footer';
import Breadcrumbs from '../../../components/ui/Breadcrumbs';
import ToolSidebar from '../../../components/layout/ToolSidebar';
import { useTheme } from '../../../contexts/ThemeContext';
import { getToolById } from '../../../data/tools';

// Импорт компонентов конвертера расстояний
import ConversionForm from './DistanceConverterPage/components/ConversionForm';
import ConversionResult from './DistanceConverterPage/components/ConversionResult';
import HistoryList from './DistanceConverterPage/components/HistoryList';
import QuickConversionSidebar from './DistanceConverterPage/components/QuickConversionSidebar';
import CommonValuesSidebar from './DistanceConverterPage/components/CommonValuesSidebar';
import RussianDistancesSidebar from './DistanceConverterPage/components/RussianDistancesSidebar';
import InfoContent from './DistanceConverterPage/components/InfoContent';

// Определение начального состояния формы
const initialFormData = {
  fromValue: '1',
  fromUnit: 'km',
  toUnit: 'm',
  precision: '2'
};

// Определение единиц измерения
export const unitDefinitions = {
  // Метрическая система
  m: { 
    id: 'm', 
    name: 'Метр', 
    namePlural: 'Метры', 
    shortName: 'м',
    system: 'metric',
    baseConversion: 1, // метр - базовая единица измерения
    category: 'common',
    description: 'Основная единица длины в Международной системе единиц (СИ)'
  },
  km: { 
    id: 'km', 
    name: 'Километр', 
    namePlural: 'Километры', 
    shortName: 'км',
    system: 'metric',
    baseConversion: 1000,
    category: 'common',
    description: 'Равен 1000 метрам, используется для измерения больших расстояний и дорог'
  },
  cm: { 
    id: 'cm', 
    name: 'Сантиметр', 
    namePlural: 'Сантиметры', 
    shortName: 'см',
    system: 'metric',
    baseConversion: 0.01,
    category: 'common',
    description: 'Равен 1/100 метра, используется в повседневных измерениях'
  },
  mm: { 
    id: 'mm', 
    name: 'Миллиметр', 
    namePlural: 'Миллиметры', 
    shortName: 'мм',
    system: 'metric',
    baseConversion: 0.001,
    category: 'common',
    description: 'Равен 1/1000 метра, используется для точных измерений'
  },
  
  // Имперская/английская система
  inch: { 
    id: 'inch', 
    name: 'Дюйм', 
    namePlural: 'Дюймы', 
    shortName: '"',
    system: 'imperial',
    baseConversion: 0.0254,
    category: 'common',
    description: 'Используется в англоязычных странах, равен примерно 2.54 см'
  },
  ft: { 
    id: 'ft', 
    name: 'Фут', 
    namePlural: 'Футы', 
    shortName: 'фт',
    system: 'imperial',
    baseConversion: 0.3048,
    category: 'common',
    description: 'Равен 12 дюймам или примерно 30.48 см'
  },
  yd: { 
    id: 'yd', 
    name: 'Ярд', 
    namePlural: 'Ярды', 
    shortName: 'ярд',
    system: 'imperial',
    baseConversion: 0.9144,
    category: 'common',
    description: 'Равен 3 футам или примерно 91.44 см'
  },
  mi: { 
    id: 'mi', 
    name: 'Миля', 
    namePlural: 'Мили', 
    shortName: 'миль',
    system: 'imperial',
    baseConversion: 1609.344,
    category: 'common',
    description: 'Сухопутная миля, равна 1609.344 метрам или 5280 футам'
  },
  
  // Морские единицы
  nmi: { 
    id: 'nmi', 
    name: 'Морская миля', 
    namePlural: 'Морские мили', 
    shortName: 'мор. миль',
    system: 'nautical',
    baseConversion: 1852,
    category: 'nautical',
    description: 'Международная морская миля, используется в навигации, равна 1852 метрам'
  },
  
  // Астрономические единицы
  au: { 
    id: 'au', 
    name: 'Астрономическая единица', 
    namePlural: 'Астрономические единицы', 
    shortName: 'а.е.',
    system: 'astronomical',
    baseConversion: 149597870700,
    category: 'astronomical',
    description: 'Среднее расстояние от Земли до Солнца, примерно 150 млн км'
  },
  ly: { 
    id: 'ly', 
    name: 'Световой год', 
    namePlural: 'Световые годы', 
    shortName: 'св. год',
    system: 'astronomical',
    baseConversion: 9460730472580800,
    category: 'astronomical',
    description: 'Расстояние, которое свет проходит за один год, примерно 9.46 трлн км'
  },
  
  // Русские исторические единицы
  verst: { 
    id: 'verst', 
    name: 'Верста', 
    namePlural: 'Версты', 
    shortName: 'верст',
    system: 'russian',
    baseConversion: 1066.8,
    category: 'russian',
    description: 'Старорусская единица измерения, равна 500 саженям или около 1.07 км'
  },
  arshin: { 
    id: 'arshin', 
    name: 'Аршин', 
    namePlural: 'Аршины', 
    shortName: 'арш',
    system: 'russian',
    baseConversion: 0.7112,
    category: 'russian',
    description: 'Старорусская единица измерения, равна примерно 71 см'
  },
  sazhen: { 
    id: 'sazhen', 
    name: 'Сажень', 
    namePlural: 'Сажени', 
    shortName: 'саж',
    system: 'russian',
    baseConversion: 2.1336,
    category: 'russian',
    description: 'Старорусская единица измерения, равна 3 аршинам или примерно 2.13 м'
  },
  vershok: { 
    id: 'vershok', 
    name: 'Вершок', 
    namePlural: 'Вершки', 
    shortName: 'верш',
    system: 'russian',
    baseConversion: 0.04445,
    category: 'russian',
    description: 'Старорусская единица измерения, равна 1/16 аршина или примерно 4.45 см'
  },
  
  // Современные российские единицы
  mkad: { 
    id: 'mkad', 
    name: 'МКАД', 
    namePlural: 'МКАДы', 
    shortName: 'МКАД',
    system: 'modern_russian',
    baseConversion: 108900,
    category: 'modern_russian',
    description: 'Длина Московской кольцевой автодороги, примерно 108.9 км'
  },
  gorzont: { 
    id: 'gorzont', 
    name: 'Горизонт', 
    namePlural: 'Горизонты', 
    shortName: 'г.',
    system: 'modern_russian',
    baseConversion: 5000,
    category: 'custom',
    description: 'Условное расстояние, видимое до линии горизонта для человека среднего роста, примерно 5 км'
  }
};

/**
 * DistanceConverterPage Component - Конвертер расстояний с поддержкой различных единиц измерения.
 */
const DistanceConverterPage = () => {
  const { subToolId } = useParams();
  const { darkMode, toggleDarkMode } = useTheme();

  // --- Данные инструмента ---
  const tool = useMemo(() => getToolById('distance-converter'), []);
  const pageTitle = tool?.metaTitle || 'Конвертер расстояний | Gensio';
  const metaDescription = tool?.metaDescription || 'Конвертируйте расстояния между различными единицами измерения: метры, километры, мили, футы, дюймы, а также исторические русские единицы измерения - версты, сажени, аршины и вершки.';

  // --- Управление состоянием ---
  const [formData, setFormData] = useState(initialFormData);
  const [result, setResult] = useState(null);
  const [conversionHistory, setConversionHistory] = useState([]);
  const [errors, setErrors] = useState({});
  const [isConverting, setIsConverting] = useState(false);
  const [favoriteConversions, setFavoriteConversions] = useState([]);

  // --- Обновление заголовка и мета-описания ---
  useEffect(() => {
    document.title = pageTitle;
    let metaTag = document.querySelector('meta[name="description"]');
    if (!metaTag) {
      metaTag = document.createElement('meta');
      metaTag.name = 'description';
      document.head.appendChild(metaTag);
    }
    metaTag.setAttribute('content', metaDescription);
  }, [pageTitle, metaDescription]);

  // --- Загрузка истории конвертаций из localStorage ---
  useEffect(() => {
    const savedHistory = localStorage.getItem('conversionHistory');
    if (savedHistory) {
      try {
        setConversionHistory(JSON.parse(savedHistory));
      } catch (e) {
        console.error("Error loading conversion history:", e);
      }
    }

    const savedFavorites = localStorage.getItem('favoriteConversions');
    if (savedFavorites) {
      try {
        setFavoriteConversions(JSON.parse(savedFavorites));
      } catch (e) {
        console.error("Error loading favorite conversions:", e);
      }
    }
  }, []);

  // --- Обработка subToolId для предварительной настройки формы ---
  useEffect(() => {
    if (!subToolId) return;

    setFormData(prev => {
      let newState = { ...initialFormData };

      switch (subToolId) {
        case 'kilometers-to-miles':
          newState.fromUnit = 'km';
          newState.toUnit = 'mi';
          break;
        case 'meters-to-feet':
          newState.fromUnit = 'm';
          newState.toUnit = 'ft';
          break;
        case 'versts-to-kilometers':
          newState.fromUnit = 'verst';
          newState.toUnit = 'km';
          break;
        case 'sazhens-to-meters':
          newState.fromUnit = 'sazhen';
          newState.toUnit = 'm';
          break;
        case 'nautical':
          newState.fromUnit = 'nmi';
          newState.toUnit = 'km';
          break;
        case 'astronomical':
          newState.fromUnit = 'au';
          newState.toUnit = 'km';
          break;
        default:
          newState = { ...prev };
      }
      return newState;
    });

    setResult(null);
    setErrors({});

  }, [subToolId]);

  // Сохранение конвертации в историю
  const saveToHistory = (conversionData) => {
    const newHistoryItem = {
      id: Date.now().toString(),
      date: new Date().toISOString(),
      ...conversionData
    };

    const updatedHistory = [newHistoryItem, ...conversionHistory.slice(0, 9)]; // Сохраняем только 10 последних
    setConversionHistory(updatedHistory);
    localStorage.setItem('conversionHistory', JSON.stringify(updatedHistory));

    return newHistoryItem;
  };

  // Добавление конвертации в избранное
  const addToFavorites = (conversionData) => {
    const existsInFavorites = favoriteConversions.some(
      fav => fav.fromUnit === conversionData.fromUnit && fav.toUnit === conversionData.toUnit
    );

    if (!existsInFavorites) {
      const newFavorite = {
        id: Date.now().toString(),
        ...conversionData
      };
      const updatedFavorites = [...favoriteConversions, newFavorite];
      setFavoriteConversions(updatedFavorites);
      localStorage.setItem('favoriteConversions', JSON.stringify(updatedFavorites));
    }
  };

  // Удаление конвертации из избранного
  const removeFromFavorites = (id) => {
    const updatedFavorites = favoriteConversions.filter(fav => fav.id !== id);
    setFavoriteConversions(updatedFavorites);
    localStorage.setItem('favoriteConversions', JSON.stringify(updatedFavorites));
  };

  // Очистка истории конвертаций
  const clearHistory = () => {
    setConversionHistory([]);
    localStorage.removeItem('conversionHistory');
  };

  // Повторение конвертации из истории
  const repeatConversion = (historyItem) => {
    setFormData({
      fromValue: historyItem.fromValue,
      fromUnit: historyItem.fromUnit,
      toUnit: historyItem.toUnit,
      precision: historyItem.precision || '2'
    });
    setResult({
      fromValue: historyItem.fromValue,
      fromUnit: historyItem.fromUnit,
      toValue: historyItem.toValue,
      toUnit: historyItem.toUnit,
      formula: historyItem.formula
    });
  };

  // --- RENDER ---
  return (
    <div className={`min-h-screen ${darkMode ? 'dark' : ''} bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white transition-colors duration-200 font-sans`}>
      {/* Background Pattern */}
      <div aria-hidden="true" className="fixed inset-0 bg-[radial-gradient(theme(colors.gray.200)_1px,transparent_1px)] dark:bg-[radial-gradient(theme(colors.gray.800)_1px,transparent_1px)] [background-size:24px_24px] opacity-20 dark:opacity-10 pointer-events-none z-[-1]"></div>

      <Header darkMode={darkMode} toggleDarkMode={toggleDarkMode} />

      <main className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumbs />

        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
          {/* --- Main Content Area --- */}
          <div className="lg:w-2/3 flex-shrink-0">
            {/* Page Header */}
            <div className="mb-8">
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-3">
                <Ruler className="w-8 h-8 text-purple-600 dark:text-purple-400 flex-shrink-0" />
                {tool?.name || 'Конвертер расстояний'}
              </h1>
              <p className="text-lg text-gray-600 dark:text-gray-400">
                {metaDescription}
              </p>
            </div>

            {/* --- Форма конвертера --- */}
            <ConversionForm 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              setErrors={setErrors}
              isConverting={isConverting}
              setIsConverting={setIsConverting}
              setResult={setResult}
              saveToHistory={saveToHistory}
              addToFavorites={addToFavorites}
              unitDefinitions={unitDefinitions}
            />

            {/* --- Результат конвертации --- */}
            {result && !isConverting && (
              <ConversionResult 
                result={result}
                unitDefinitions={unitDefinitions}
              />
            )}

            {/* --- История конвертаций --- */}
            <HistoryList 
              history={conversionHistory}
              clearHistory={clearHistory}
              repeatConversion={repeatConversion}
              unitDefinitions={unitDefinitions}
            />

            {/* --- Информационный контент о единицах измерения --- */}
            <InfoContent unitDefinitions={unitDefinitions} />
          </div>

          {/* --- Sidebar Area --- */}
          <aside className="lg:w-1/3 self-start space-y-8">
            {/* --- Панель быстрой конвертации --- */}
            <QuickConversionSidebar 
              formData={formData}
              setFormData={setFormData}
              unitDefinitions={unitDefinitions}
              isConverting={isConverting}
              errors={errors}
              setResult={setResult}
              saveToHistory={saveToHistory}
            />

            {/* --- Популярные значения --- */}
            <CommonValuesSidebar 
              setFormData={setFormData}
              unitDefinitions={unitDefinitions}
            />

            {/* --- Русские единицы измерения --- */}
            <RussianDistancesSidebar 
              setFormData={setFormData}
              unitDefinitions={unitDefinitions}
            />

            {/* --- Связанные инструменты --- */}
            {tool && (
              <ToolSidebar 
                currentToolId={tool.id} 
                currentSubToolId={subToolId} 
                tool={tool} 
              />
            )}
          </aside>
        </div>
      </main>

      <Footer />

      {/* --- Global Styles --- */}
      <style jsx global>{`
        /* Base Input Styles */
        .form-input, .form-select, .form-textarea {
          @apply block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm text-gray-900 dark:text-white transition duration-150 ease-in-out disabled:opacity-60 disabled:cursor-not-allowed dark:placeholder-gray-400;
        }
        .form-checkbox {
          @apply h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-purple-600 focus:ring-purple-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        .form-radio {
          @apply h-4 w-4 border-gray-300 dark:border-gray-600 text-purple-600 focus:ring-purple-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        /* Error State Styling */
        .input-error {
          @apply border-red-500 dark:border-red-500 text-red-900 dark:text-red-300 placeholder-red-400 dark:placeholder-red-600 focus:border-red-500 dark:focus:border-red-500 focus:ring-red-500 dark:focus:ring-red-500;
        }
      `}</style>
    </div>
  );
};

export default DistanceConverterPage;